import { type Dispatch, useReducer } from 'react'
import type {
  BusinessFlow,
  BusinessFlowStep,
  ConceptEntity,
  ConceptModel,
  ConceptRelation,
  Demand,
  KnowledgeGraph,
  Requirement
} from '@/types/knowledge_graph'

export const EMPTY_KG: KnowledgeGraph = {
  schema_version: 1,
  concept_model: { entities: [], relations: [] },
  demands: [],
  requirements: [],
  business_flows: []
}

export type RequirementsReviewAction =
  | { type: 'RESET', kg: KnowledgeGraph }
  | { type: 'ADD_REQUIREMENT', requirement: Requirement }
  | { type: 'UPDATE_REQUIREMENT', requirementId: string, changes: Partial<Requirement> }
  | { type: 'REMOVE_REQUIREMENT', requirementId: string }
  | { type: 'ADD_DEMAND', demand: Demand }
  | { type: 'UPDATE_DEMAND', demandId: string, changes: Partial<Demand> }
  | { type: 'REMOVE_DEMAND', demandId: string }
  | { type: 'ADD_BUSINESS_FLOW', businessFlow: BusinessFlow }
  | { type: 'UPDATE_BUSINESS_FLOW', businessFlowId: string, changes: Partial<BusinessFlow> }
  | { type: 'REMOVE_BUSINESS_FLOW', businessFlowId: string }
  | { type: 'ADD_BUSINESS_FLOW_STEP', businessFlowId: string, step: BusinessFlowStep }
  | { type: 'UPDATE_BUSINESS_FLOW_STEP', businessFlowId: string, stepId: string, changes: Partial<BusinessFlowStep> }
  | { type: 'REMOVE_BUSINESS_FLOW_STEP', businessFlowId: string, stepId: string }
  | { type: 'REORDER_BUSINESS_FLOW_STEPS', businessFlowId: string, fromIndex: number, toIndex: number }
  | { type: 'ADD_CONCEPT_ENTITY', entity: ConceptEntity }
  | { type: 'UPDATE_CONCEPT_ENTITY', entityId: string, changes: Partial<ConceptEntity> }
  | { type: 'REMOVE_CONCEPT_ENTITY', entityId: string }
  | { type: 'ADD_CONCEPT_RELATION', relation: ConceptRelation }
  | { type: 'REMOVE_CONCEPT_RELATION', relationId: string }

export type HistoryAction =
  | RequirementsReviewAction
  | { type: 'UNDO' }
  | { type: 'REDO' }

export interface HistoryState {
  past: KnowledgeGraph[]
  present: KnowledgeGraph
  future: KnowledgeGraph[]
}

const MAX_HISTORY = 50

export function kgReducer (state: KnowledgeGraph, action: RequirementsReviewAction): KnowledgeGraph {
  switch (action.type) {
    case 'RESET':
      return action.kg

    case 'ADD_REQUIREMENT':
      return { ...state, requirements: [...(state.requirements ?? []), action.requirement] }

    case 'UPDATE_REQUIREMENT':
      return {
        ...state,
        requirements: (state.requirements ?? []).map((rq) =>
          rq.id === action.requirementId ? { ...rq, ...action.changes } : rq
        )
      }

    case 'REMOVE_REQUIREMENT':
      return { ...state, requirements: (state.requirements ?? []).filter((rq) => rq.id !== action.requirementId) }

    case 'ADD_DEMAND':
      return { ...state, demands: [...(state.demands ?? []), action.demand] }

    case 'UPDATE_DEMAND':
      return {
        ...state,
        demands: (state.demands ?? []).map((d) =>
          d.id === action.demandId ? { ...d, ...action.changes } : d
        )
      }

    case 'REMOVE_DEMAND':
      return { ...state, demands: (state.demands ?? []).filter((d) => d.id !== action.demandId) }

    case 'ADD_BUSINESS_FLOW':
      return { ...state, business_flows: [...(state.business_flows ?? []), action.businessFlow] }

    case 'UPDATE_BUSINESS_FLOW':
      return {
        ...state,
        business_flows: (state.business_flows ?? []).map((f) =>
          f.id === action.businessFlowId ? { ...f, ...action.changes } : f
        )
      }

    case 'REMOVE_BUSINESS_FLOW':
      return {
        ...state,
        business_flows: (state.business_flows ?? []).filter((f) => f.id !== action.businessFlowId)
      }

    case 'ADD_BUSINESS_FLOW_STEP':
      return {
        ...state,
        business_flows: (state.business_flows ?? []).map((f) =>
          f.id === action.businessFlowId
            ? { ...f, steps: [...(f.steps ?? []), action.step] }
            : f
        )
      }

    case 'UPDATE_BUSINESS_FLOW_STEP':
      return {
        ...state,
        business_flows: (state.business_flows ?? []).map((f) =>
          f.id === action.businessFlowId
            ? {
                ...f,
                steps: (f.steps ?? []).map((s) =>
                  s.id === action.stepId ? { ...s, ...action.changes } : s
                )
              }
            : f
        )
      }

    case 'REMOVE_BUSINESS_FLOW_STEP':
      return {
        ...state,
        business_flows: (state.business_flows ?? []).map((f) =>
          f.id === action.businessFlowId
            ? { ...f, steps: (f.steps ?? []).filter((s) => s.id !== action.stepId) }
            : f
        )
      }

    case 'REORDER_BUSINESS_FLOW_STEPS': {
      return {
        ...state,
        business_flows: (state.business_flows ?? []).map((f) => {
          if (f.id !== action.businessFlowId) return f
          const steps = [...(f.steps ?? [])]
          const [moved] = steps.splice(action.fromIndex, 1)
          steps.splice(action.toIndex, 0, moved)
          return { ...f, steps }
        })
      }
    }

    case 'ADD_CONCEPT_ENTITY': {
      const cm: ConceptModel = state.concept_model ?? { entities: [], relations: [] }
      return { ...state, concept_model: { ...cm, entities: [...cm.entities, action.entity] } }
    }

    case 'UPDATE_CONCEPT_ENTITY': {
      const cm: ConceptModel = state.concept_model ?? { entities: [], relations: [] }
      return {
        ...state,
        concept_model: {
          ...cm,
          entities: cm.entities.map((e) => e.id === action.entityId ? { ...e, ...action.changes } : e)
        }
      }
    }

    case 'REMOVE_CONCEPT_ENTITY': {
      const cm: ConceptModel = state.concept_model ?? { entities: [], relations: [] }
      const removedName = cm.entities.find((e) => e.id === action.entityId)?.name ?? ''
      return {
        ...state,
        concept_model: {
          ...cm,
          entities: cm.entities.filter((e) => e.id !== action.entityId),
          relations: cm.relations.filter((r) => r.from !== removedName && r.to !== removedName)
        }
      }
    }

    case 'ADD_CONCEPT_RELATION': {
      const cm: ConceptModel = state.concept_model ?? { entities: [], relations: [] }
      return { ...state, concept_model: { ...cm, relations: [...cm.relations, action.relation] } }
    }

    case 'REMOVE_CONCEPT_RELATION': {
      const cm: ConceptModel = state.concept_model ?? { entities: [], relations: [] }
      return {
        ...state,
        concept_model: { ...cm, relations: cm.relations.filter((r) => r.id !== action.relationId) }
      }
    }

    default:
      return state
  }
}

export function historyReducer (state: HistoryState, action: HistoryAction): HistoryState {
  if (action.type === 'UNDO') {
    if (state.past.length === 0) return state
    const previous = state.past[state.past.length - 1]
    return {
      past: state.past.slice(0, -1),
      present: previous,
      future: [state.present, ...state.future]
    }
  }
  if (action.type === 'REDO') {
    if (state.future.length === 0) return state
    const next = state.future[0]
    return {
      past: [...state.past, state.present].slice(-MAX_HISTORY),
      present: next,
      future: state.future.slice(1)
    }
  }
  const newPresent = kgReducer(state.present, action)
  if (newPresent === state.present) return state
  return {
    past: [...state.past, state.present].slice(-MAX_HISTORY),
    present: newPresent,
    future: []
  }
}

export function useKnowledgeGraphHistory (initial: KnowledgeGraph): {
  kg: KnowledgeGraph
  historyState: HistoryState
  dispatch: Dispatch<HistoryAction>
} {
  const [historyState, dispatch] = useReducer(historyReducer, {
    past: [],
    present: initial,
    future: []
  })
  return { kg: historyState.present, historyState, dispatch }
}
